import React, { useEffect, useState } from "react";
import { Drawer, Skeleton, Result } from "antd";
import { Badge } from "components/ui/badge";
import { TrendingUp, ExternalLink } from "lucide-react";
import { getInstitutionSocialImpactDetail } from "Explore/Services/Overview";
import { getSdgIconUrl } from "./constants";

const SdgDetailDrawer = ({
  institutionId,
  sdgNum,
  isOpen,
  onClose,
  fetchDetail,
  rankLabel = "National Rank",
  shareLabel = "Global Share",
}) => {
  const [data, setData] = useState(null);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState(null);

  useEffect(() => {
    if (!isOpen || !sdgNum || (!institutionId && !fetchDetail)) {
      setData(null);
      return;
    }

    const controller = new AbortController();

    const fetchData = async () => {
      setIsLoading(true);
      setError(null);
      try {
        let res;
        if (fetchDetail) {
          res = await fetchDetail(sdgNum, controller.signal);
        } else if (institutionId) {
          res = await getInstitutionSocialImpactDetail(institutionId, sdgNum);
        }
        setData(res);
      } catch (err) {
        if (err.name === "CanceledError" || err.name === "AbortError" || err?.code === "ERR_CANCELED") {
          return;
        }
        const errorDetail = err?.response?.data?.detail?.errors?.[0]?.msg 
          || err?.response?.data?.message 
          || err?.message 
          || "Failed to load SDG details";
        setError(errorDetail);
      } finally {
        setIsLoading(false);
      }
    };

    fetchData();

    return () => {
      controller.abort();
    };
  }, [isOpen, sdgNum, institutionId, fetchDetail]);

  const sdg = data?.sdg;
  const stats = data?.stats;
  const topPapers = data?.top_papers || [];
  const keywords = data?.keywords || [];

  const imageUrl = getSdgIconUrl(sdg?.sdg_num) || sdg?.photo_url || (sdg?.sdg_num ? `https://sdgs.un.org/sites/default/files/goals/E_SDG_Icons-${String(sdg.sdg_num).padStart(2, '0')}.jpg` : "");

  return (
    <Drawer
      title={sdg ? `SDG ${sdg.sdg_num}: ${sdg.name}` : "SDG Details"}
      placement="right"
      onClose={onClose}
      open={isOpen}
      width={450}
      zIndex={9999}
    >
      {isLoading ? (
        <div className="space-y-6">
          <Skeleton active paragraph={{ rows: 4 }} />
          <Skeleton active paragraph={{ rows: 3 }} />
          <Skeleton active paragraph={{ rows: 5 }} />
        </div>
      ) : error ? (
        <Result status="error" title="Failed to load" subTitle={error} />
      ) : !data || stats?.papers === 0 ? (
        <Result 
          status="warning" 
          title="No data available" 
          subTitle="No publications found for this SDG with the current filters." 
        />
      ) : (
        <div className="flex flex-col gap-6">
          {/* Header section */}
          <div className="flex gap-4 items-start">
            {imageUrl && (
              <img 
                src={imageUrl} 
                alt={`SDG ${sdg?.sdg_num}`} 
                className="w-24 h-24 rounded shadow-sm object-contain"
                onError={(e) => {
                  if (sdg?.sdg_num) {
                    const unFallback = `https://sdgs.un.org/sites/default/files/goals/E_SDG_Icons-${String(sdg.sdg_num).padStart(2, '0')}.jpg`;
                    if (e.target.src !== unFallback) {
                      e.target.src = unFallback;
                    }
                  }
                }}
              />
            )}
            <div className="flex-1 space-y-2">
              <p className="text-sm text-foreground/90">{sdg?.description}</p>
              {Boolean(sdg?.related_links?.un_site || sdg?.related_links?.un_link) && (
                <a 
                  href={sdg.related_links.un_site || sdg.related_links.un_link} 
                  target="_blank" 
                  rel="noopener noreferrer"
                  className="inline-flex items-center gap-1 text-xs text-explore hover:underline"
                >
                  UN Official Page <ExternalLink className="w-3 h-3" />
                </a>
              )}
            </div>
          </div>

          {/* Stats section */}
          {stats && (
            <div className="grid grid-cols-2 gap-3 p-4 bg-muted/30 rounded-lg border">
              <div>
                <p className="text-xs text-muted-foreground mb-1">Publications</p>
                <p className="text-xl font-bold">{stats.papers != null ? stats.papers.toLocaleString() : "—"}</p>
              </div>
              <div>
                <p className="text-xs text-muted-foreground mb-1">{rankLabel}</p>
                <p className="text-xl font-bold">{stats.rank && stats.rank > 0 ? `#${stats.rank}` : "—"}</p>
              </div>
              <div>
                <p className="text-xs text-muted-foreground mb-1">{shareLabel}</p>
                <p className="font-semibold">{stats.share_pct != null ? `${Number(stats.share_pct).toFixed(2)}%` : "—"}</p>
              </div>
              <div>
                <p className="text-xs text-muted-foreground mb-1">Growth</p>
                {stats.growth_pct != null ? (
                  <p className={`font-semibold flex items-center gap-1 ${stats.growth_pct > 0 ? "text-green-600" : stats.growth_pct < 0 ? "text-destructive" : "text-muted-foreground"}`}>
                    {stats.growth_pct > 0 && <TrendingUp className="w-3 h-3" />}
                    {stats.growth_pct > 0 ? "+" : ""}{Number(stats.growth_pct).toFixed(1)}%
                  </p>
                ) : (
                  <p className="font-semibold text-muted-foreground">—</p>
                )}
              </div>
            </div>
          )}

          {/* Keywords */}
          {keywords.length > 0 && (
            <div className="space-y-2">
              <h4 className="font-semibold text-sm">Top Keywords</h4>
              <div className="flex flex-wrap gap-2">
                {keywords.map((kw, idx) => (
                  <Badge key={kw.id || idx} variant="secondary" className="font-normal">
                    {kw.keyword} ({kw.papers})
                  </Badge>
                ))}
              </div>
            </div>
          )}

          {/* Top Papers */}
          {topPapers.length > 0 && (
            <div className="space-y-3">
              <h4 className="font-semibold text-sm">Most Cited Papers</h4>
              <div className="flex flex-col gap-2">
                {topPapers.map((paper, idx) => (
                  <div key={paper.id || idx} className="p-3 border rounded-lg hover:border-explore/50 transition-colors">
                    <p className="text-sm font-medium leading-tight mb-2">{paper.title}</p>
                    <div className="flex justify-between items-center text-xs text-muted-foreground">
                      <span>{paper.year}</span>
                      <span>{paper.citation_count != null ? `${paper.citation_count.toLocaleString()} citations` : ""}</span>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          )}
          
          {/* Targets */}
          {sdg?.targets?.length > 0 && (
            <div className="space-y-3">
              <h4 className="font-semibold text-sm">UN Targets</h4>
              <div className="flex flex-col gap-3">
                {sdg.targets.map((target, idx) => (
                  <div key={target.target || idx} className="flex gap-3">
                    <Badge variant="outline" className="h-fit shrink-0 font-mono">{target.target}</Badge>
                    <p className="text-xs leading-relaxed text-foreground/80">{target.description}</p>
                  </div>
                ))}
              </div>
            </div>
          )}

        </div>
      )}
    </Drawer>
  );
};

export default SdgDetailDrawer;
